<script lang="ts" setup>
import { toastServiceError } from '@/services/core/notification';
import WorkScheduleService from '@/services/api/WorkScheduleService';
import { useUiStore } from '@/stores';
import { Permission, ScheduleWeekday, WorkScheduleModel } from '@/types';
import { computed, onMounted, ref, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

const route = useRoute();
const uiStore = useUiStore();
const { t } = useI18n();

const workSchedule: Ref<WorkScheduleModel | undefined> = ref(undefined);

const weekdays: ScheduleWeekday[] = [
    ScheduleWeekday.SUNDAY,
    ScheduleWeekday.MONDAY,
    ScheduleWeekday.TUESDAY,
    ScheduleWeekday.WEDNESDAY,
    ScheduleWeekday.THURSDAY,
    ScheduleWeekday.FRIDAY,
    ScheduleWeekday.SATURDAY,
];

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const fetchWorkScheduleById = async (id: number) => {
    try {
        setLoading(true);
        const { data } = await WorkScheduleService.get<WorkScheduleModel>(id);
        workSchedule.value = data;
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

const workDays = computed(() =>
    weekdays.filter((day: ScheduleWeekday) => workSchedule.value?.[day]),
);

const formatDayLabel = (day: ScheduleWeekday) => t('schedule.labels.' + day.toLocaleLowerCase());

onMounted(() => {
    const id = Number(route.params.id);
    if (!isNaN(id)) fetchWorkScheduleById(id);
});
</script>

<template>
    <div class="space-y-4 p-4 sm:p-6 lg:p-8">
        <UiPageHeader>
            <template #info>
                <h1 class="text-xl font-semibold text-gray-800">
                    {{ $t('workSchedule.view.title') }}
                </h1>
            </template>
            <template #actions>
                <div class="flex space-x-4">
                    <UiBackButton :fallbackTo="{ name: 'ListWorkSchedule' }" />
                    <router-link
                        v-if="$can(Permission.EDIT_WORK_SCHEDULE)"
                        :to="{
                            name: 'EditWorkScheduleById',
                            params: {
                                id: route.params.id,
                            },
                        }"
                        class="block hover:bg-gray-50"
                    >
                        <UiButton variant="primary" text-variant="white">
                            {{ $t('core.actions.Edit') }}
                        </UiButton>
                    </router-link>
                </div>
            </template>
        </UiPageHeader>

        <div class="space-y-4">
            <UiPanel>
                <div class="gap-4 sm:grid-cols-2 md:grid lg:grid-cols-3">
                    <div>
                        <h3 class="mb-1 font-semibold">
                            {{ $t('workSchedule.labels.name') }}
                        </h3>
                        <p>{{ workSchedule?.name }}</p>
                    </div>
                    <div>
                        <h3 class="mb-1 font-semibold">
                            {{ $t('workSchedule.labels.timeBegin') }}
                        </h3>
                        <p>{{ workSchedule?.timeBegin.substring(0, 5) }}</p>
                    </div>
                    <div>
                        <h3 class="mb-1 font-semibold">
                            {{ $t('workSchedule.labels.timeEnd') }}
                        </h3>
                        <p>{{ workSchedule?.timeEnd.substring(0, 5) }}</p>
                    </div>
                    <div class="sm:col-span-2 lg:col-span-3">
                        <h3 class="mb-1 font-semibold">
                            {{ $t('workSchedule.labels.workDays') }}
                        </h3>
                        <div class="space-x-2 text-xs text-gray-500">
                            <span
                                v-for="day in workDays"
                                :key="day"
                                class="rounded border bg-white px-1.5 py-0.5"
                            >
                                {{ formatDayLabel(day) }}
                            </span>
                        </div>
                    </div>
                </div>
                <UiAuditStatusInfo
                    :model-value="workSchedule"
                    class="bt-1 mt-4 border-t border-gray-300 pt-2"
                />
            </UiPanel>
        </div>
    </div>
</template>
